import { c as _c } from "react/compiler-runtime";
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Div } from '../helpers/StyledUtils';
import Text from './Text';
import Tooltip from './Tooltip';
interface Props {
  chips: ReactNode[];
}

// Renders a single row of chips at their natural width (no ellipsis), showing as many as fit
// in the available space. The remaining chips collapse into a "+N more" label that reveals
// them in a tooltip.
const ChipOverflow: React.FC<Props> = t0 => {
  const $ = _c(17);
  const {
    chips
  } = t0;
  const container_ref = useRef(null);
  const measure_ref = useRef(null);
  const [visible_count, setVisibleCount] = useState(chips.length);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const container = container_ref.current;
      const measure = measure_ref.current;
      if (!container || !measure) {
        return;
      }
      const compute = () => {
        const available_width = container.getBoundingClientRect().width;
        const measure_left = measure.getBoundingClientRect().left;
        const children = Array.from(measure.children) as HTMLElement[];
        const more_label = children[children.length - 1];
        const chip_right_edges = children.slice(0, -1).map(child => child.getBoundingClientRect().right - measure_left);
        if (chip_right_edges.length === 0) {
          return;
        }
        if (chip_right_edges[chip_right_edges.length - 1] <= available_width) {
          setVisibleCount(chip_right_edges.length);
          return;
        }
        const gap = parseFloat(getComputedStyle(measure).columnGap) || 0;
        const more_width = more_label.getBoundingClientRect().width;
        let fit_count = 0;
        while (fit_count < chip_right_edges.length && chip_right_edges[fit_count] + gap + more_width <= available_width - 1) {
          fit_count++;
        }
        setVisibleCount(Math.max(fit_count, 1));
      };
      compute();
      const observer = new ResizeObserver(compute);
      observer.observe(container);
      observer.observe(measure);
      return () => observer.disconnect();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== chips) {
    t2 = [chips];
    $[1] = chips;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  if (chips.length === 0) {
    return null;
  }
  let t3;
  if ($[3] !== chips || $[4] !== visible_count) {
    const hidden_chips = chips.slice(visible_count);
    let t4;
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        gap: 2,
        align: "center"
      };
      t5 = {
        position: "relative"
      };
      $[6] = t4;
      $[7] = t5;
    } else {
      t4 = $[6];
      t5 = $[7];
    }
    let t6;
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        gap: 2,
        align: "center"
      };
      t7 = {
        position: "absolute",
        top: 0,
        left: 0,
        width: "max-content",
        visibility: "hidden",
        pointerEvents: "none"
      };
      $[8] = t6;
      $[9] = t7;
    } else {
      t6 = $[8];
      t7 = $[9];
    }
    let t8;
    if ($[10] !== chips) {
      t8 = chips.map(_temp);
      $[10] = chips;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    const t9 = chips.length - 1;
    let t10;
    if ($[12] !== t9) {
      t10 = <Text size="s" semi={true} muted={true} text_wrap={false}>+{t9} more</Text>;
      $[12] = t9;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    let t11;
    if ($[14] !== t10 || $[15] !== t8) {
      t11 = <Div ref={measure_ref} aria-hidden={true} flex={t6} style={t7}>{t8}{t10}</Div>;
      $[14] = t10;
      $[15] = t8;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    t3 = <Div ref={container_ref} w={100} flex={t4} style={t5}>{t11}{chips.slice(0, visible_count).map(_temp2)}{hidden_chips.length > 0 && <Div style={{
        flex: "none"
      }}><Tooltip side="bottom" max_width={284} tooltip={<Div flex={{
          gap: 2,
          align: "center",
          wrap: true
        }}>{hidden_chips.map(_temp3)}</Div>}><Text size="s" semi={true} muted={true} text_wrap={false} style={{
            textDecoration: "underline",
            cursor: "default"
          }}>+{hidden_chips.length} more</Text></Tooltip></Div>}</Div>;
    $[3] = chips;
    $[4] = visible_count;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export default ChipOverflow;
function _temp(chip, index) {
  return <Div key={index} style={{
    flex: "none"
  }}>{chip}</Div>;
}
function _temp2(chip_0, index_0) {
  return <Div key={index_0} style={{
    flex: "0 1 auto",
    minWidth: 0
  }}>{chip_0}</Div>;
}
function _temp3(chip_1, index_1) {
  return <Div key={index_1}>{chip_1}</Div>;
}